/**
 * Lot 41A/C — Types pour les QuestTemplate persistents en DB et
 * structure typée des champs JSON (prerequisites / rewards / conditions).
 *
 * Le backend stocke ces JSON tels quels (jsonb). Le typage cote front
 * sert uniquement a l'edition guidee dans le Quest Editor — d'autres
 * services peuvent y ranger des cles libres.
 */

// ============================================================================
// Modeles persistes (alignes sur QuestTemplateModel/StepModel cote backend)
// ============================================================================

/** P2.4 -- Faction sponsoring (synchrone avec EFactionType cote back). */
export type SponsoringFaction = 'Player' | 'Empire' | 'Bandits';

export interface QuestTemplateModel {
  id: number;
  code: string;
  label: string;
  orderIndex: number;
  description: string;
  questTypeId: number;
  imageCode: string | null;
  difficulty: number;
  durationDays: number | null;
  pointsAward: number;
  prerequisitesJson: string;
  rewardsJson: string;
  isActive: boolean;
  /** P2.4 -- faction qui sponsorise la quete. Player = neutre. */
  sponsoringFaction: SponsoringFaction;
}

export interface QuestTemplateStepModel {
  id: number;
  questTemplateId: number;
  orderIndex: number;
  label: string;
  description: string;
  eventKey: string;
  targetCount: number;
  conditionsJson: string;
  rewardsJson: string;
  isOptional: boolean;
}

// ============================================================================
// PDTOs (envoye au backend)
// ============================================================================

export interface QuestTemplatePDTO {
  code: string;
  label: string;
  orderIndex: number;
  description: string;
  questTypeId: number;
  imageCode: string | null;
  difficulty: number;
  durationDays: number | null;
  pointsAward: number;
  prerequisitesJson: string;
  rewardsJson: string;
  isActive: boolean;
  /** P2.4 -- faction sponsoring. Optionnel cote client : si absent,
   *  le backend assume Player (neutre). */
  sponsoringFaction?: SponsoringFaction;
}

export interface QuestTemplateStepPDTO {
  questTemplateId: number;
  orderIndex: number;
  label: string;
  description: string;
  eventKey: string;
  targetCount: number;
  conditionsJson: string;
  rewardsJson: string;
  isOptional: boolean;
}

// ============================================================================
// Structures typées des champs JSON
// ============================================================================

/**
 * Rewards typés. Le QuestTemplate.rewardsJson stocke ces champs (recompenses
 * finales de quete). Le QuestTemplateStep.rewardsJson stocke les recompenses
 * intermediaires de fin d'etape.
 */
export interface QuestRewards {
  flouz?: number;
  points?: number;
  items?: { code: string; qty: number }[];
  resources?: { code: string; qty: number }[];
  /** P2.4 -- delta alignment applique au complete (clampe [-100..+100]). */
  alignmentDelta?: number;
}

/**
 * Prerequisites typés. Stocke dans QuestTemplate.prerequisitesJson.
 * Tous les champs sont optionnels — vide = aucun prerequis.
 */
export interface QuestPrerequisites {
  /** Codes de quetes a avoir complete avant de pouvoir accepter celle-ci. */
  completedQuests?: string[];
  /** Niveaux minimum de recherches (code -> level). */
  minResearch?: { code: string; level: number }[];
  /** Niveaux minimum de batiments (code -> level). */
  minBuilding?: { code: string; level: number }[];
  /** Score combine minimum (combat + economique). */
  minScore?: number;
  /** Grade militaire minimum (ID, cf MilitaryRank). */
  minMilitaryRankId?: number;
}

/**
 * Conditions typées pour le filtrage d'un event sur une etape.
 * Les cles sont libres mais on documente les plus courantes. Cote runtime
 * (Lot 41D) le matching applique tous les filtres par AND.
 */
export interface QuestStepConditions {
  /** Cible : si renseigne, ne compte que les events impliquant cette alliance. */
  targetAllianceId?: number;
  /** Cible : si renseigne, ne compte que les events impliquant ce joueur. */
  targetPlayerId?: number;
  /** Cible : si renseigne, ne compte que les events sur cette planete. */
  targetPlanetId?: number;
  /** Resource concernee (resource.deliver, item.archaeology). */
  resourceCode?: string;
  /** Valeur minimum (resource.deliver : quantite min ; ship.kill : taille min). */
  minValue?: number;
  /** Categorie de ship cible (ship.kill : "Leger" | "Lourd" | "Capital"). */
  shipCategory?: string;
}

// ============================================================================
// Helpers parse/serialize
// ============================================================================

export function parseRewards(json: string): QuestRewards {
  if (!json) return {};
  try { return JSON.parse(json) as QuestRewards; } catch { return {}; }
}

export function parsePrerequisites(json: string): QuestPrerequisites {
  if (!json) return {};
  try { return JSON.parse(json) as QuestPrerequisites; } catch { return {}; }
}

export function parseConditions(json: string): QuestStepConditions {
  if (!json) return {};
  try { return JSON.parse(json) as QuestStepConditions; } catch { return {}; }
}

/** Serialise en JSON en omettant les champs vides/zero (pour rester compact). */
export function serializeRewards(r: QuestRewards): string {
  const out: Record<string, unknown> = {};
  if (r.flouz && r.flouz > 0) out.flouz = r.flouz;
  if (r.points && r.points > 0) out.points = r.points;
  if (r.items && r.items.length > 0) out.items = r.items;
  if (r.resources && r.resources.length > 0) out.resources = r.resources;
  if (r.alignmentDelta && r.alignmentDelta !== 0) out.alignmentDelta = r.alignmentDelta;
  return JSON.stringify(out);
}

export function serializePrerequisites(p: QuestPrerequisites): string {
  const out: Record<string, unknown> = {};
  if (p.completedQuests && p.completedQuests.length > 0) out.completedQuests = p.completedQuests;
  if (p.minResearch && p.minResearch.length > 0) out.minResearch = p.minResearch;
  if (p.minBuilding && p.minBuilding.length > 0) out.minBuilding = p.minBuilding;
  if (p.minScore && p.minScore > 0) out.minScore = p.minScore;
  if (p.minMilitaryRankId && p.minMilitaryRankId > 0) out.minMilitaryRankId = p.minMilitaryRankId;
  return JSON.stringify(out);
}

export function serializeConditions(c: QuestStepConditions): string {
  const out: Record<string, unknown> = {};
  if (c.targetAllianceId && c.targetAllianceId > 0) out.targetAllianceId = c.targetAllianceId;
  if (c.targetPlayerId && c.targetPlayerId > 0) out.targetPlayerId = c.targetPlayerId;
  if (c.targetPlanetId && c.targetPlanetId > 0) out.targetPlanetId = c.targetPlanetId;
  if (c.resourceCode) out.resourceCode = c.resourceCode;
  if (c.minValue && c.minValue > 0) out.minValue = c.minValue;
  if (c.shipCategory) out.shipCategory = c.shipCategory;
  return JSON.stringify(out);
}

// ============================================================================
// Suggestions UI
// ============================================================================

/** Catalogue connu d'event keys (synchronise avec QuestCatalog.cs cote backend). */
export const EVENT_KEY_SUGGESTIONS: { value: string; label: string }[] = [
  { value: 'ship.kill',        label: 'Combat — detruire un vaisseau' },
  { value: 'system.explore',   label: 'Exploration — decouvrir un systeme' },
  { value: 'resource.deliver', label: 'Livraison — acheminer des ressources' },
  { value: 'mission.spy',      label: 'Espionnage — reussir une mission' },
  { value: 'fleet.escort',     label: 'Escorte — proteger une flotte' },
  { value: 'item.archaeology', label: 'Fouille — trouver un artefact' },
  { value: 'treaty.sign',      label: 'Diplomatie — signer un traite' },
  { value: 'fleet.rescue',     label: 'Sauvetage — recuperer une flotte' },
];

export const DIFFICULTY_OPTIONS: { value: number; label: string }[] = [
  { value: 1, label: '1 — Trivial' },
  { value: 2, label: '2 — Facile' },
  { value: 3, label: '3 — Moyen' },
  { value: 4, label: '4 — Difficile' },
  { value: 5, label: '5 — Legendaire' },
];
